import { InterestRate } from "@justhome/api-core";
import { colorsRGB, lines } from "./constants";
import { InterestRateChartType, Period } from "./types";

/**
 * Gets a theme color in rgba.
 *
 * @param name Name of the color
 * @param alpha Alpha value
 * @returns The color as a rgba string
 */
export const color = (name: keyof typeof colorsRGB, alpha = 1) =>
  `rgba(${colorsRGB[name]} / ${alpha})`;

const formatValue = (chartType: InterestRateChartType, value: number) => {
  if (chartType === "rate" || chartType === "budget") {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  } else {
    return `${value.toFixed(2)} %`;
  }
};

const getLegendLabel = (chartType: InterestRateChartType) => {
  switch (chartType) {
    case "interest":
      return "Zinsentwicklung";
    case "rate":
      return "Ratenentwicklung";
    case "budget":
      return "Budgetentwicklung";
    default:
      return "";
  }
};

export const getDatasetConfig = (
  hidden: boolean,
  chartType: InterestRateChartType,
  line: (typeof lines)[number],
  data: InterestRate[],
) => {
  return {
    hidden,
    fill: true,
    label: `${getLegendLabel(chartType)} - ${line.label}`,
    data: data
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      .map((item) => item[line.id]),
    borderColor: color(line.color),
    borderWidth: 1,
    pointRadius: 0,
    pointHoverRadius: 0,
    pointBackgroundColor: color(line.color),
    pointBorderColor: color(line.color),
    backgroundColor: (ctx: any) => {
      const chart = ctx.chart;
      const { ctx: chartCtx, chartArea } = chart;

      if (!chartArea) {
        return null;
      }

      const gradient = chartCtx.createLinearGradient(
        0,
        chartArea.top,
        0,
        chartArea.bottom,
      );
      gradient.addColorStop(0, color(line.color, 0.25));
      gradient.addColorStop(1, color(line.color, 0.025));
      return gradient;
    },
    stepped: true,
  };
};

const getIntervalDivider = (period: Period) => {
  const intervalInMonths = {
    "6m": 12,
    "12m": 12,
    "60m": 10,
    "120m": 10,
    "180m": 15,
  }[period];

  return intervalInMonths;
};

export const getOptions = (
  period: Period,
  chartType: InterestRateChartType,
) => ({
  animation: {
    duration: 300,
  },
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        filter: (legendItem: any) => !legendItem.hidden,
      },
    },
    crosshair: {
      line: {
        color: "rgba(200, 200, 200, 0.8)",
        width: 1,
      },
      sync: {
        enabled: false,
      },
      zoom: {
        enabled: false,
      },
      snap: {
        enabled: true,
      },
    },
    tooltip: {
      enabled: true,
      intersect: false,
      mode: "index" as const,
      position: "nearest" as const,
      callbacks: {
        label: function (context: any) {
          const label = context.dataset.label || "";
          const value = context.raw || 0;

          return `${label}: ${formatValue(chartType, value)}`;
        },
      },
    },
    custom: {
      chartType: chartType,
    },
  },
  scales: {
    y: {
      type: "linear" as const,
      ticks: {
        callback: function (tickValue: number | string) {
          return formatValue(chartType, Number(tickValue));
        },
        maxRotation: 0,
        autoSkip: true,
      },
    },
    x: {
      type: "category" as const,
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 45,
        callback(
          this: any,
          tickValue: string | number,
          index: number,
          ticks: any[],
        ): string | null {
          const totalLabels = ticks.length;
          const intervalDivider = getIntervalDivider(period);
          const interval = Math.ceil(totalLabels / intervalDivider);

          const isMobile = this.chart.width < 600;

          if (isMobile) {
            return index === 0 || index === totalLabels - 1
              ? this.getLabelForValue(tickValue)
              : null;
          }

          return index % interval === 0
            ? this.getLabelForValue(tickValue)
            : null;
        },
      },
      grid: {
        display: true,
        drawOnChartArea: false,
        drawTicks: true,
        tickMarkLength: 10,
      },
    },
  },
});

export const filterDataByPeriod = (
  data: InterestRate[],
  period: Period,
): InterestRate[] => {
  const now = new Date();
  const periodInMonths = {
    "6m": 6,
    "12m": 12,
    "60m": 60,
    "120m": 120,
    "180m": 180,
  }[period];

  const startDate = new Date(
    now.getFullYear(),
    now.getMonth() - periodInMonths,
    now.getDate(),
  );

  return data
    .filter((item) => {
      const itemDate = new Date(item.createdAt);
      return itemDate >= startDate && itemDate <= now;
    })
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
};
